import {
	isOnboardingSettled,
	SIGNUP_ATTRIBUTION_ALREADY_RECORDED,
	type SignupAttributionChannel,
} from "pages/auth/signupAttribution/attributionOptions";
import { useAuthStore } from "store/auth.store";
import useSWR from "swr";
import { api } from "utils/axios";
import { posthog } from "utils/posthog";

type OnboardingRes = {
	onboardingCompleted: boolean;
	attributionRequired?: boolean;
};

const fetcher = async ([url]: [string, string | null]) => {
	const res = await api.get<OnboardingRes>(url);
	return res.data;
};

export function useOnboarding(revalidateOnFocus = false) {
	// Keyed by uid: a second account signing up in the same tab must not read
	// the previous account's cached answer.
	const uid = useAuthStore((state) => state.uid);
	const { data, error, isLoading, isValidating, mutate } = useSWR<
		OnboardingRes,
		unknown,
		[string, string | null]
	>(["user/onboarding", uid], fetcher, {
		revalidateOnFocus,
		// Bounded: the gate waits while these run (see isOnboardingSettled).
		errorRetryCount: 4,
	});

	const complete = async () => {
		await api.patch("user/onboarding");
		await mutate();
	};

	const submitAttribution = async (
		channel: SignupAttributionChannel,
		otherText: string | null,
	) => {
		try {
			const res = await api.patch<OnboardingRes>(
				"user/onboarding/attribution",
				{ channel, otherText },
			);
			await mutate(res.data, { revalidate: false });
		} catch (err) {
			// Already answered (a double click, another tab): done, not an error.
			// The API's exception filter nests the payload under `message`.
			const data = (
				err as {
					response?: { data?: { code?: string; message?: { code?: string } } };
				}
			)?.response?.data;
			const code = data?.message?.code ?? data?.code;
			if (code === SIGNUP_ATTRIBUTION_ALREADY_RECORDED) {
				await mutate();
				return;
			}
			throw err;
		}
		// The channel mix is otherwise readable only by a human with a production
		// admin login clicking Export on the admin Users page (EQU-52).
		//
		// `channel` only. `otherText` is free text the user typed and does not go
		// to a third party.
		//
		// 🪤 Deliberately AFTER the try/catch, not inside it. Both early exits
		// above — a failed PATCH (rethrown) and the already-recorded path (a double
		// click or a second tab) — must fire nothing. One answer, one event.
		//
		// 🪤 This is the ONLY emitter for an answered gate, and it is deliberately
		// here rather than at the call site. `SignupAttribution` cannot make the
		// guarantee above: `await submitAttribution(...)` returns without throwing
		// on BOTH a saved answer and an already-recorded one, so a capture there
		// counts the second click as a second answer. Two such call-site emitters
		// used to exist, under two other names, and a single click reached PostHog
		// three times (EQU-401). Keep the count here, behind the write.
		try {
			posthog.capture("signup_attribution_submitted", { channel });
		} catch {
			// Analytics must never break an answer the API already stored.
		}
	};

	return {
		onboardingCompleted: data?.onboardingCompleted,
		attributionRequired: data?.attributionRequired === true,
		settled: isOnboardingSettled({ data, error, isValidating }),
		isLoading,
		revalidate: () => mutate(),
		complete,
		submitAttribution,
	};
}
